export interface TaskPermissions {
  /**
   * Case of read === true and show === false can be useful if
   * configurator scripts require some data to work properly, but
   * this data shouldn’t be shown to user. Another case is creating mirrors.
   */
  show: boolean // if can be shown to user on client

  delete: boolean
  editTitle: boolean
  editDescription: boolean
  close: boolean // archive tasks
  assignUsers: 'no' | 'add-self' | 'set-self' | 'change-from-self' | 'yes'
  connect: boolean
  editSubtasks: 'no' | 'complete' | 'yes'
  editStickers: boolean
  editPins: boolean
  move: 'no' | 'board' | 'project' | 'yes'

  complete: boolean | undefined

  sendMessages: boolean
  sendFiles: boolean
  editWhoToNotify: 'no' | 'self' | 'yes'
}

export const isDisabledTaskProp = (
  obj: TaskPermissions,
  key: keyof TaskPermissions
): boolean => {
  if (key === 'show') {
    return false
  } else {
    if (!obj.show) {
      return true
    }
    if (key === 'sendFiles') {
      if (!obj.sendMessages) {
        return true
      }
    }
    return false
  }
}

export const getDefaultTaskPermissions = (): TaskPermissions => {
  return {
    show: true,
    delete: true,
    editTitle: true,
    editDescription: true,
    close: true,
    assignUsers: 'yes',
    connect: true,
    editSubtasks: 'yes',
    editStickers: true,
    editPins: true,
    move: 'yes',
    complete: true,

    sendMessages: true,
    sendFiles: true,
    editWhoToNotify: 'yes'
  }
}

export const allowAllOnTask = (perm: TaskPermissions): TaskPermissions => {
  return {
    ...perm,
    show: true,
    delete: true,
    editTitle: true,
    editDescription: true,
    complete: true,
    close: true,
    assignUsers: 'yes',
    connect: true,
    editSubtasks: 'yes',
    editStickers: true,
    editPins: true,
    move: 'yes',
    sendMessages: true,
    sendFiles: true,
    editWhoToNotify: 'yes'
  }
}

export const disallowAllOnTask = (perm: TaskPermissions): TaskPermissions => {
  return {
    ...perm,
    ...nothingAllowedTaskPermissions()
  }
}

export const nothingAllowedTaskPermissions = (): TaskPermissions => ({
  show: false,
  delete: false,
  editTitle: false,
  editDescription: false,
  complete: false,
  close: false,
  assignUsers: 'no',
  connect: false,
  editSubtasks: 'no',
  editStickers: false,
  editPins: false,
  move: 'no',
  sendMessages: false,
  sendFiles: false,
  editWhoToNotify: 'no'
})

export const isCorrectTaskPermissions = (p: TaskPermissions): boolean => {
  return !!p && !!p.move
}

export const combineTaskPermissions = (
  a: TaskPermissions | undefined,
  b: TaskPermissions | undefined
): TaskPermissions | undefined => {
  if (!a) {
    return b
  }
  if (!b) {
    return a
  }
  const moveOrder: Array<TaskPermissions['move']> = [
    'no',
    'board',
    'project',
    'yes'
  ]
  const assignUsersOrder: Array<TaskPermissions['assignUsers']> = [
    'no',
    'add-self',
    'set-self',
    'change-from-self',
    'yes'
  ]
  const editWhoToNotifyOrder: Array<TaskPermissions['editWhoToNotify']> = [
    'no',
    'self',
    'yes'
  ]
  const editSubtasksOrder: Array<TaskPermissions['editSubtasks']> = [
    'no',
    'complete',
    'yes'
  ]
  const moveIx = Math.max(
    0,
    moveOrder.indexOf(a.move),
    moveOrder.indexOf(b.move)
  )
  const assignUsersIx = Math.max(
    0,
    assignUsersOrder.indexOf(a.assignUsers),
    assignUsersOrder.indexOf(b.assignUsers)
  )
  const editWhoToNotifyIx = Math.max(
    0,
    editWhoToNotifyOrder.indexOf(a.editWhoToNotify),
    editWhoToNotifyOrder.indexOf(b.editWhoToNotify)
  )
  const editSubtasksIx = Math.max(
    0,
    editSubtasksOrder.indexOf(a.editSubtasks),
    editSubtasksOrder.indexOf(b.editSubtasks)
  )

  return {
    show: a.show || b.show,
    delete: a.delete || b.delete,

    editTitle: a.editTitle || b.editTitle,
    editDescription: a.editDescription || b.editDescription,
    complete: a.complete || b.complete,
    close: a.close || b.close,
    assignUsers: assignUsersOrder[assignUsersIx],
    connect: a.connect || b.connect,
    editSubtasks: editSubtasksOrder[editSubtasksIx],
    editStickers: a.editStickers || b.editStickers,
    editPins: a.editPins || b.editPins,
    move: moveOrder[moveIx],

    sendMessages: a.sendMessages || b.sendMessages,
    sendFiles: a.sendFiles || b.sendFiles,
    editWhoToNotify: editWhoToNotifyOrder[editWhoToNotifyIx]
  }
}
